import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Phone, Clock, Globe, Mic, FileText,
  AlertTriangle, CheckCircle, Volume2, Trophy, XCircle, 
  Calendar, PhoneMissed, User
} from 'lucide-react';
import { getCalls } from '../services/api';
import { formatDate, formatDuration, callTypeBadge, urgencyBadge, sentimentEmoji } from '../utils';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const OUTCOMES = [
  { key: 'won', label: 'Won', icon: Trophy, color: 'var(--green)', bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.3)' },
  { key: 'lost', label: 'Lost', icon: XCircle, color: '#ef4444', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)' },
  { key: 'follow_up', label: 'Follow Up', icon: Calendar, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)' },
  { key: 'no_answer', label: 'No Answer', icon: PhoneMissed, color: 'var(--text-muted)', bg: 'var(--bg-primary)', border: 'var(--border)' },
];

function authHeaders() {
  const token = localStorage.getItem('cia_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

export default function CallDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [outcomeSuccess, setOutcomeSuccess] = useState('');

  useEffect(() => {
    loadCall();
  }, [id]);

  const loadCall = () => {
    getCalls().then(res => {
      const found = (res.data.calls || []).find(c => c.id === id);
      setCall(found || null);
      // Fetch fresh recording URL if call has a recording
      if (found && (found.recording_url || found.provider_recording_id)) {
        fetchRecordingUrl();
      }
    }).catch(console.error)
      .finally(() => setLoading(false));
  };

  const fetchRecordingUrl = async () => {
    try {
      const token = localStorage.getItem('cia_token');
      const res = await fetch(`${API_BASE}/calls/recording-url/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.url) {
        setRecordingUrl(data.url);
        console.log('[RECORDING] Got fresh URL');
      }
    } catch (e) {
      console.log('[RECORDING] Failed to get URL:', e);
    }
  };

  const handleOutcome = async (outcomeKey) => {
    if (savingOutcome) return;
    setSavingOutcome(outcomeKey);
    try {
      const res = await fetch(`${API_BASE}/calls/${id}/outcome`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ call_outcome: outcomeKey }),
      });
      const result = await res.json();
      if (result.success) {
        setCall(prev => ({ ...prev, call_outcome: outcomeKey }));
        setOutcomeSuccess(OUTCOMES.find(o => o.key === outcomeKey)?.label || '');
        setTimeout(() => setOutcomeSuccess(''), 3000);
      }
    } catch (e) {
      console.error('Outcome update error:', e);
    }
    setSavingOutcome(false);
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!call) return (
    <div style={{ padding: 28 }}>
      <button className="btn btn-ghost" onClick={() => navigate('/calls')}>
        <ArrowLeft size={14} /> Back to Calls
      </button>
      <div className="empty-state">
        <Phone size={32} />
        <p>Call not found</p>
      </div>
    </div>
  );

  const typeBadge = callTypeBadge(call.call_type, call);
  const urgBadge = urgencyBadge(call.urgency);
  const currentOutcome = OUTCOMES.find(o => o.key === call.call_outcome);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 900 }}>
      {/* Back */}
      <button
        className="btn btn-ghost"
        onClick={() => navigate('/calls')}
        style={{ marginBottom: 20 }}
      >
        <ArrowLeft size={14} /> Back to Calls
      </button>

      {/* Header */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: 24,
        marginBottom: 20,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
              {call.caller_name || call.caller_number || 'Unknown Caller'}
            </h1>
            {call.caller_company && (
              <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{call.caller_company}</p>
            )}
            <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
              {formatDate(call.created_at)}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className={`badge ${typeBadge.class}`}>{typeBadge.label}</span>
            <span className={`badge ${urgBadge.class}`}>{urgBadge.label}</span>
            <span style={{ fontSize: 20 }}>{sentimentEmoji(call.sentiment)}</span>
            {currentOutcome && (
              <span style={{
                fontSize: 12, fontWeight: 600,
                color: currentOutcome.color,
                background: currentOutcome.bg,
                border: `1px solid ${currentOutcome.border}`,
                padding: '3px 10px', borderRadius: 99,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <currentOutcome.icon size={12} />
                {currentOutcome.label}
              </span>
            )}
          </div>
        </div>

        {/* Meta row */}
        <div style={{
          display: 'flex',
          gap: 24,
          marginTop: 20,
          paddingTop: 16,
          borderTop: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}>
          {[
            { icon: Phone, label: 'Number', value: call.caller_number || '—' },
            { icon: Clock, label: 'Duration', value: formatDuration(call.duration) },
            { icon: Globe, label: 'Language', value: call.language_detected || 'English' },
            { icon: Mic, label: 'Channel', value: call.channel || 'Phone' },
            ...(call.agent_name ? [{ icon: User, label: 'Agent', value: call.agent_name }] : []),
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <item.icon size={14} color="var(--text-muted)" />
              <div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{item.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Call Outcome */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: 20,
        marginBottom: 20,
      }}>
        <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 }}>
          Call Outcome
        </h2>

        {outcomeSuccess && (
          <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={13} /> Marked as {outcomeSuccess}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {OUTCOMES.map(outcome => {
            const isActive = call.call_outcome === outcome.key;
            const isLoading = savingOutcome === outcome.key;
            return (
              <button
                key={outcome.key}
                onClick={() => handleOutcome(outcome.key)}
                disabled={!!savingOutcome}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: `1px solid ${isActive ? outcome.border : 'var(--border)'}`,
                  background: isActive ? outcome.bg : 'var(--bg-primary)',
                  color: isActive ? outcome.color : 'var(--text-secondary)',
                  fontSize: 13, fontWeight: isActive ? 600 : 400,
                  cursor: savingOutcome ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s',
                  opacity: savingOutcome && !isLoading ? 0.5 : 1,
                }}
              >
                {isLoading ? (
                  <span style={{ width: 13, height: 13, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                ) : (
                  <outcome.icon size={13} />
                )}
                {outcome.label}
              </button>
            );
          })}
        </div>

        {!call.call_outcome && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
            Mark the outcome of this call to track your team's conversion rate.
          </p>
        )}
      </div>

      {/* Summary & Action */}
      {call.summary && (
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderLeft: '3px solid var(--blue)',
          borderRadius: 'var(--radius)',
          padding: 20,
          marginBottom: 20,
        }}>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            AI Summary
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {call.summary}
          </p>
        </div>
      )}

      {call.suggested_action && (
        <div style={{
          background: 'rgba(16,185,129,0.08)',
          border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 'var(--radius)',
          padding: 16,
          marginBottom: 20,
          display: 'flex',
          gap: 12,
        }}>
          <CheckCircle size={18} color="var(--green)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--green)', marginBottom: 4 }}>
              SUGGESTED ACTION
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-primary)' }}>{call.suggested_action}</p>
          </div>
        </div>
      )}

      {/* Audio Player */}
      {(call.recording_url || call.provider_recording_id) && (
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: 20,
          marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Volume2 size={16} color="var(--cyan)" />
            <h2 style={{ fontSize: 13, fontWeight: 600 }}>Call Recording</h2>
          </div>
          {recordingUrl ? (
            <audio
              controls
              src={recordingUrl}
              style={{ width: '100%', borderRadius: 6 }}
              onError={(e) => {
                console.log('[AUDIO] Error, refetching URL...');
                fetchRecordingUrl();
              }}
            />
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
              Loading recording...
            </div>
          )}
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            Recordings available for 30 days
          </p>
        </div>
      )}

      {/* Transcript */}
      {(call.transcript_english || call.transcript_original) && (
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: 20,
          marginBottom: 20,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileText size={16} color="var(--cyan)" />
              <h2 style={{ fontSize: 13, fontWeight: 600 }}>Transcript</h2>
            </div>
            {call.transcript_original && call.transcript_english !== call.transcript_original && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className={!showOriginal ? 'btn btn-primary' : 'btn btn-ghost'}
                  style={{ fontSize: 11, padding: '4px 10px' }}
                  onClick={() => setShowOriginal(false)}
                >English</button>
                <button
                  className={showOriginal ? 'btn btn-primary' : 'btn btn-ghost'}
                  style={{ fontSize: 11, padding: '4px 10px' }}
                  onClick={() => setShowOriginal(true)}
                >Original</button>
              </div>
            )}
          </div>
          <div style={{
            fontSize: 13,
            lineHeight: 1.9,
            color: 'var(--text-secondary)',
            whiteSpace: 'pre-wrap',
            fontFamily: showOriginal ? 'inherit' : 'var(--mono)',
            maxHeight: 400,
            overflow: 'auto',
            padding: 16,
            background: 'var(--bg-primary)',
            borderRadius: 6,
            border: '1px solid var(--border)',
          }}>
            {showOriginal ? call.transcript_original : (call.transcript_english || call.transcript_original)}
          </div>
        </div>
      )}

      {/* Follow-up */}
      {call.follow_up_required && (
        <div style={{
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: 'var(--radius)',
          padding: 16,
          display: 'flex',
          gap: 12,
        }}>
          <AlertTriangle size={18} color="var(--orange)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange)', marginBottom: 4 }}>
              FOLLOW-UP REQUIRED
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              This call requires follow-up. Check your contacts for the scheduled reminder.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
